import BaseStyle from '../assets/css/Dashboard.module.css';
import NotificationsStyle from '../assets/css/Notifications.module.css';
import HomeStyle from "../assets/css/Home.module.css";

import React, {useCallback, useEffect, useRef, useState} from "react";
import {useTheme} from "../provider/themeProvider";
import {useAppContext} from "../provider/appProvider";
import {useDesktop} from "../provider/DesktopContext";
import NotificationElement from "../components/NotificationElement";
import api from "../services/api";

const PAGE_SIZE = 20;

// Distance au bas de la liste à partir de laquelle on charge la page suivante.
const SCROLL_THRESHOLD = 200;

const FILTERS = [
  { key: 'unread', label: 'Non lues' },
  { key: 'all', label: 'Toutes' },
  { key: 'alertes', label: 'Alertes' },
  { key: 'information', label: 'Information' },
  { key: 'equipements', label: 'Équipements' },
];

const buildQuery = (filter, beforeId) => {
  const params = new URLSearchParams({ limit: String(PAGE_SIZE) });

  if (beforeId) {
    params.set('beforeId', String(beforeId));
  }

  if (filter === 'unread') {
    params.set('unread', 'true');
  } else if (filter !== 'all') {
    params.set('category', filter);
  }

  return `/notifications?${params.toString()}`;
};

const Notifications = () => {
  // 'Non lues' est le filtre par défaut, comme à l'ouverture de la vue mobile.
  const [activeFilter, setActiveFilter] = useState('unread');
  const [items, setItems] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(false);

  const containerRef = useRef(null);

  const { darkMode } = useTheme();
  const { notificationCounts, setNotifications, fetchNotificationCounts } = useAppContext();
  const isDesktop = useDesktop();

  const counts = notificationCounts;

  const fetchPage = useCallback(async (filter, beforeId) => {
    const response = await api.get(buildQuery(filter, beforeId));
    return response.data.notifications;
  }, []);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(false);

      try {
        const list = await fetchPage(activeFilter);

        if (cancelled) {
          return;
        }

        // Comme dans l'app : plutôt que d'afficher un écran vide à l'ouverture, on
        // bascule sur 'Toutes' si l'utilisateur n'a aucune notification non lue.
        if (list.length === 0 && activeFilter === 'unread') {
          setActiveFilter('all');
          return;
        }

        // L'ordre est celui du serveur : sans filtre, les alertes non lues d'abord.
        setItems(list);
        setHasMore(list.length >= PAGE_SIZE);
      } catch (err) {
        console.error("Failed to fetch notifications", err);
        if (!cancelled) {
          setItems([]);
          setHasMore(false);
          setError(true);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    load();
    fetchNotificationCounts();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeFilter, fetchPage]);

  const loadMore = useCallback(async () => {
    if (loadingMore || !hasMore || items.length === 0) {
      return;
    }

    setLoadingMore(true);

    try {
      const more = await fetchPage(activeFilter, items[items.length - 1].id);

      setItems((current) => [...current, ...more]);
      setHasMore(more.length >= PAGE_SIZE);
    } catch (err) {
      console.error("Failed to load more notifications", err);
      setHasMore(false);
    } finally {
      setLoadingMore(false);
    }
  }, [activeFilter, fetchPage, hasMore, items, loadingMore]);

  // Le conteneur de page défile lui-même (le shell est en height: 100dvh) : c'est
  // sur lui qu'il faut écouter, la fenêtre ne défile jamais.
  useEffect(() => {
    const container = containerRef.current;

    if (!container) {
      return;
    }

    const handleScroll = () => {
      const remaining = container.scrollHeight - container.scrollTop - container.clientHeight;

      if (remaining <= SCROLL_THRESHOLD) {
        loadMore();
      }
    };

    container.addEventListener('scroll', handleScroll);

    return () => {
      container.removeEventListener('scroll', handleScroll);
    };
  }, [loadMore]);

  const markLocally = (notificationID, field) => {
    setItems((current) => current.map((notification) => {
      return notification.id === notificationID ? { ...notification, [field]: new Date() } : notification;
    }));
  };

  const handleRead = (notificationID) => {
    markLocally(notificationID, 'readed_at');
    fetchNotificationCounts();
  };

  const handleReadAll = async () => {
    try {
      await api.patch('/notifications/read');

      const markAll = (list) => list.map((notification) => ({
        ...notification,
        readed_at: notification.readed_at ?? new Date(),
        seen_at: notification.seen_at ?? new Date(),
      }));

      setItems(markAll);
      setNotifications(markAll);
      fetchNotificationCounts();
    } catch (err) {
      console.error("Failed to mark all notifications as read", err);
    }
  };

  // Tant que /notifications/count n'a pas répondu, aucun filtre n'est grisé : sinon la
  // barre s'affiche entièrement désactivée le temps du premier chargement.
  const countsLoaded = Object.keys(counts.totalByCategory ?? {}).length > 0;

  const isDisabled = (filter) => {
    if (filter === 'all' || !countsLoaded) {
      return false;
    }

    if (filter === 'unread') {
      return (counts.total ?? 0) === 0;
    }

    return (counts.totalByCategory?.[filter] ?? 0) === 0;
  };

  const unreadAlerts = counts.byCategory?.alertes ?? 0;

  const readAllButton = (
    <span className={NotificationsStyle.readAll} onClick={handleReadAll}>Marquer comme lu</span>
  );

  return (
    <>
      {isDesktop &&
        <div className={BaseStyle.header}>
          <div className={BaseStyle.left}>
            <div className={BaseStyle.iconContainer}>
              <img src={`https://cdn.codnum.fr/watch4safe/assets/images/notification_${darkMode ? 'black' : 'white'}.svg`} alt="" height="20" width="20"/>
            </div>
            <div className={BaseStyle.description}>
              <span className={BaseStyle.title}>Notifications</span>
              <span className={BaseStyle.subTitle}>Toutes vos notifications</span>
            </div>
          </div>
          <div className={BaseStyle.right}>
            {readAllButton}
          </div>
        </div>
      }
      {!isDesktop &&
        <div className={NotificationsStyle.mobileHeader}>
          <span className={HomeStyle.title}>Notifications</span>
          {readAllButton}
        </div>
      }

      <div className={NotificationsStyle.filters}>
        {FILTERS.map((filter) => {
          const disabled = isDisabled(filter.key);
          const selected = activeFilter === filter.key;

          return (
            <button
              key={filter.key}
              type="button"
              className={`${NotificationsStyle.chip} ${selected ? NotificationsStyle.chipSelected : ''}`}
              disabled={disabled}
              onClick={() => setActiveFilter(filter.key)}
            >
              {filter.label}
              {filter.key === 'alertes' && unreadAlerts > 0 &&
                <span className={NotificationsStyle.chipBadge}>{unreadAlerts}</span>
              }
            </button>
          );
        })}
      </div>

      <div className={BaseStyle.contentContainerY} ref={containerRef}>
        {loading &&
          <span className={NotificationsStyle.message}>Chargement…</span>
        }

        {!loading && error &&
          <span className={NotificationsStyle.error}>Impossible de charger les notifications.</span>
        }

        {!loading && !error && items.length === 0 &&
          <span className={NotificationsStyle.message}>Aucune notification pour le moment</span>
        }

        {!loading && !error &&
          <div className={NotificationsStyle.list}>
            {items.map((notification) => {
              return (
                <NotificationElement
                  key={notification.id}
                  id={notification.id}
                  callback={notification.callback}
                  readed={notification.readed_at}
                  seen={notification.seen_at}
                  type={notification.type}
                  category={notification.category}
                  title={notification.title}
                  description={notification.description}
                  calledBy={notification.userCalled ? `${notification.userCalled.first_name} ${notification.userCalled.last_name}` : null}
                  createdAt={new Date(notification.createdAt)}
                  thumbnail={notification.thumbnailLink}
                  fullDate={true}
                  highlight={notification.category === 'alertes' && activeFilter === 'all'}
                  onSee={() => markLocally(notification.id, 'seen_at')}
                  onRead={handleRead}
                />
              );
            })}
          </div>
        }

        {loadingMore &&
          <span className={NotificationsStyle.message}>Chargement…</span>
        }
      </div>
    </>
  );
};

export default Notifications;
